/**
 * @fileoverview Pre-Appointment Statuses manager (MOR-882)
 *
 * Firm-defined pipeline stages that act as kanban columns and the pipeline
 * state of every pre-appointment job (e.g. Referral / Enquiry → Conflict
 * check → … → Appointed / Declined), ordered top-to-bottom (the board column
 * order). Admin/owner only — `canEdit` gates writes; mutations enforce the
 * same rule. The seeded stages are a starting point and can be fully
 * replaced; the server only refuses to remove the last Open / Won / Lost
 * stage, so the firm always has a complete pipeline.
 *
 * Visual language mirrors the Task Statuses manager for consistency.
 */

import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { convexQuery, useConvexMutation } from "@convex-dev/react-query";
import {
  DndContext,
  PointerSensor,
  closestCenter,
  useSensor,
  useSensors,
  type DragEndEvent,
} from "@dnd-kit/core";
import {
  SortableContext,
  arrayMove,
  useSortable,
  verticalListSortingStrategy,
} from "@dnd-kit/sortable";
import { CSS } from "@dnd-kit/utilities";
import { api } from "@cvx/_generated/api";
import { extractErrorMessage } from "@/utils/errors";
import type { Id } from "@cvx/_generated/dataModel";
import type { FunctionReturnType } from "convex/server";
import type { PreAppointmentStatusType } from "@cvx/preAppointment/preAppointmentStatuses/schema";
import type { TaskColor } from "@cvx/lib/taskColor.schema";
import {
  ArrowDown,
  ArrowUp,
  GripVertical,
  Loader2,
  Lock,
  Pencil,
  Plus,
  Trash2,
  X,
} from "lucide-react";
import { toast } from "sonner";
import { Badge, Button } from "@/ui/primitives";
import { Input } from "@/ui/primitives/input";
import { Label } from "@/ui/primitives/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/ui/primitives/select";
import { cn } from "@/utils/misc";
import { ColorSwatchPicker } from "../tasks/ColorSwatchPicker";
import { STATUS_TYPE_OPTIONS } from "./status-types";

const GROUP_CARD_CLASS =
  "rounded-xl bg-background shadow-[0_1px_3px_rgba(0,0,0,0.08),0_1px_2px_rgba(0,0,0,0.06)] hover:shadow-[0_4px_12px_rgba(0,0,0,0.1),0_2px_4px_rgba(0,0,0,0.06)] transition-shadow duration-200 overflow-hidden";

type PreAppointmentStatus = FunctionReturnType<
  typeof api.preAppointment.statuses.listByOrg
>[number];

export function PreAppointmentStatusesManager({
  orgId,
  canEdit,
}: {
  orgId: Id<"organizations">;
  canEdit: boolean;
}) {
  const { data: statuses } = useQuery(
    convexQuery(api.preAppointment.statuses.listByOrg, { orgId }),
  );
  const reorder = useConvexMutation(api.preAppointment.statuses.reorder);
  const [showNewForm, setShowNewForm] = useState(false);
  const rows = useMemo(() => statuses ?? [], [statuses]);

  // Flat list ordered by each status's own sortOrder — this is the pipeline
  // board column order top-to-bottom.
  const orderedRows = useMemo(
    () => [...rows].sort((a, b) => a.sortOrder - b.sortOrder),
    [rows],
  );

  // Drag-to-reorder (mirrors the task statuses manager). A 6px activation
  // distance keeps row clicks/buttons usable; only the grip handle starts a
  // drag.
  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 6 } }),
  );

  const handleDragEnd = (e: DragEndEvent) => {
    const { active, over } = e;
    if (!over || active.id === over.id) return;
    const ids = orderedRows.map((s) => s._id);
    const from = ids.indexOf(active.id as Id<"preAppointmentStatuses">);
    const to = ids.indexOf(over.id as Id<"preAppointmentStatuses">);
    if (from < 0 || to < 0) return;
    reorder({ orgId, orderedIds: arrayMove(ids, from, to) }).catch(
      (err: Error) => toast.error(err.message || "Could not reorder stages."),
    );
  };

  return (
    <div className="flex w-full flex-col gap-6">
      {!canEdit && (
        <div className="flex items-start gap-3 rounded-xl border border-amber-200/60 bg-amber-50 px-4 py-3 text-amber-900 dark:border-amber-900/40 dark:bg-amber-950/30 dark:text-amber-200">
          <Lock className="mt-0.5 size-4 shrink-0" />
          <div className="text-sm">
            <p className="font-medium">Read-only view</p>
            <p className="mt-0.5 text-xs opacity-80">
              You need owner or admin access to manage pipeline stages.
            </p>
          </div>
        </div>
      )}

      <div className={GROUP_CARD_CLASS}>
        <div className="flex items-start justify-between gap-6 border-b border-border/50 p-6">
          <div className="min-w-0 flex-1">
            <h1 className="text-lg font-semibold text-primary">
              Pipeline Stages
            </h1>
            <p className="mt-1 text-sm text-primary/60">
              Set the stages of your pre-appointment pipeline. Drag to reorder
              stages. You can remove any stage as long as one Open, Won and Lost
              stage remains.
            </p>
          </div>
          <Badge variant="secondary" className="shrink-0">
            {rows.length} {rows.length === 1 ? "stage" : "stages"}
          </Badge>
        </div>

        <div className="flex items-center justify-between border-b border-border/50 px-6 py-4">
          <div>
            <h4 className="text-sm font-semibold text-primary">Stages</h4>
            <p className="text-xs text-primary/40">
              Ordered top-to-bottom — this is the board column order.
            </p>
          </div>
          {canEdit && (
            <Button
              variant="outline"
              size="sm"
              onClick={() => setShowNewForm(true)}
              className="gap-1.5"
            >
              <Plus className="size-3.5" />
              Add stage
            </Button>
          )}
        </div>

        <div className="divide-y divide-border/30">
          <DndContext
            sensors={sensors}
            collisionDetection={closestCenter}
            onDragEnd={handleDragEnd}
          >
            <SortableContext
              items={orderedRows.map((s) => s._id)}
              strategy={verticalListSortingStrategy}
            >
              {orderedRows.map((status, index) => (
                <StatusRow
                  key={status._id}
                  orgId={orgId}
                  status={status}
                  canEdit={canEdit}
                  isFirst={index === 0}
                  isLast={index === orderedRows.length - 1}
                  orderedIds={orderedRows.map((s) => s._id)}
                  flatIndex={index}
                />
              ))}
            </SortableContext>
          </DndContext>

          {rows.length === 0 && !showNewForm && (
            <div className="px-6 py-8 text-center">
              <p className="text-sm text-primary/40">No stages yet.</p>
            </div>
          )}

          {showNewForm && (
            <NewStatusForm
              orgId={orgId}
              onClose={() => setShowNewForm(false)}
            />
          )}
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Status rows
// ---------------------------------------------------------------------------

function StatusRow({
  orgId,
  status,
  canEdit,
  isFirst,
  isLast,
  orderedIds,
  flatIndex,
}: {
  orgId: Id<"organizations">;
  status: PreAppointmentStatus;
  canEdit: boolean;
  /** First/last in the ordered list — controls reorder button state. */
  isFirst: boolean;
  isLast: boolean;
  orderedIds: Array<Id<"preAppointmentStatuses">>;
  flatIndex: number;
}) {
  const [editing, setEditing] = useState(false);
  const update = useConvexMutation(api.preAppointment.statuses.update);
  const reorder = useConvexMutation(api.preAppointment.statuses.reorder);
  const archive = useConvexMutation(api.preAppointment.statuses.archive);

  const {
    attributes,
    listeners,
    setNodeRef,
    setActivatorNodeRef,
    transform,
    transition,
    isDragging,
  } = useSortable({ id: status._id, disabled: !canEdit || editing });
  const style = {
    transform: CSS.Transform.toString(transform),
    transition,
  };

  const move = (direction: -1 | 1) => {
    const next = [...orderedIds];
    const target = flatIndex + direction;
    if (target < 0 || target >= next.length) return;
    [next[flatIndex], next[target]] = [next[target], next[flatIndex]];
    reorder({ orgId, orderedIds: next }).catch((e: Error) =>
      toast.error(e.message || "Could not reorder stages."),
    );
  };

  if (editing) {
    return (
      <div ref={setNodeRef} style={style} className="px-6 py-4">
        <StatusForm
          initial={{
            name: status.name,
            type: status.type,
            color: status.color,
          }}
          submitLabel="Save"
          onCancel={() => setEditing(false)}
          onSubmit={async (state) => {
            await update({
              statusId: status._id,
              patch: {
                name: state.name.trim(),
                type: state.type,
                color: state.color,
              },
            });
            setEditing(false);
          }}
        />
      </div>
    );
  }

  const typeLabel = STATUS_TYPE_OPTIONS.find(
    (t) => t.value === status.type,
  )?.label;

  return (
    <div
      ref={setNodeRef}
      style={style}
      className={cn(
        "flex items-center gap-3 bg-background px-6 py-3",
        isDragging && "relative z-10 shadow-lg",
      )}
    >
      {canEdit && (
        <span
          ref={setActivatorNodeRef}
          {...attributes}
          {...listeners}
          className="cursor-grab touch-none text-primary/25 hover:text-primary/60 active:cursor-grabbing"
          title="Drag to reorder"
          aria-label={`Drag ${status.name} to reorder`}
        >
          <GripVertical className="size-4" />
        </span>
      )}
      {canEdit && (
        <div className="flex flex-col">
          <button
            type="button"
            disabled={isFirst}
            onClick={() => move(-1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move up"
          >
            <ArrowUp className="size-3" />
          </button>
          <button
            type="button"
            disabled={isLast}
            onClick={() => move(1)}
            className="text-primary/30 hover:text-primary/70 disabled:opacity-30"
            aria-label="Move down"
          >
            <ArrowDown className="size-3" />
          </button>
        </div>
      )}
      <Badge color={status.color} variant="soft" className="shrink-0">
        {status.name}
      </Badge>
      <div className="min-w-0 flex-1">
        <div className="text-xs text-primary/40">
          {typeLabel}
          {status.isSystem ? " · System" : ""}
        </div>
      </div>
      {canEdit && (
        <div className="flex items-center gap-1">
          <Button
            size="icon"
            variant="ghost"
            className="size-7"
            onClick={() => setEditing(true)}
          >
            <Pencil className="size-3.5" />
          </Button>
          <Button
            size="icon"
            variant="ghost"
            className="size-7 text-destructive hover:text-destructive"
            title={`Remove ${status.name}`}
            onClick={() => {
              archive({ statusId: status._id })
                .then((res) => {
                  toast.success(
                    res.jobsUnstaged > 0
                      ? `Removed ${status.name}. ${res.jobsUnstaged} job${res.jobsUnstaged === 1 ? "" : "s"} moved to Unstaged.`
                      : `Removed ${status.name}.`,
                  );
                })
                .catch((e: Error) =>
                  toast.error(e.message || "Could not remove stage."),
                );
            }}
          >
            <Trash2 className="size-3.5" />
          </Button>
        </div>
      )}
    </div>
  );
}

function NewStatusForm({
  orgId,
  onClose,
}: {
  orgId: Id<"organizations">;
  onClose: () => void;
}) {
  const create = useConvexMutation(api.preAppointment.statuses.create);
  return (
    <div className="bg-muted/20 px-6 py-4">
      <StatusForm
        initial={{ name: "", type: "open", color: "gray" }}
        submitLabel="Add stage"
        onCancel={onClose}
        onSubmit={async (state) => {
          await create({
            orgId,
            name: state.name.trim(),
            type: state.type,
            color: state.color,
          });
          onClose();
        }}
      />
    </div>
  );
}

interface StatusFormState {
  name: string;
  type: PreAppointmentStatusType;
  color: TaskColor;
}

function StatusForm({
  initial,
  submitLabel,
  onCancel,
  onSubmit,
}: {
  initial: StatusFormState;
  submitLabel: string;
  onCancel: () => void;
  onSubmit: (state: StatusFormState) => Promise<void>;
}) {
  const [state, setState] = useState<StatusFormState>(initial);
  const [submitting, setSubmitting] = useState(false);

  const submit = async () => {
    if (!state.name.trim()) {
      toast.error("Stage name is required.");
      return;
    }
    setSubmitting(true);
    try {
      await onSubmit(state);
    } catch (e) {
      toast.error(extractErrorMessage(e, "Could not save stage."));
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="flex flex-col gap-3">
      <div className="grid grid-cols-2 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            Name
          </Label>
          <Input
            value={state.name}
            autoFocus
            onChange={(e) => setState({ ...state, name: e.target.value })}
            placeholder="Initial consult, Engagement letter sent…"
          />
        </div>
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            Type
          </Label>
          <Select
            value={state.type}
            onValueChange={(v) =>
              setState({ ...state, type: v as PreAppointmentStatusType })
            }
          >
            <SelectTrigger>
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {STATUS_TYPE_OPTIONS.map((t) => (
                <SelectItem key={t.value} value={t.value}>
                  {t.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <div className="grid grid-cols-1 gap-3">
        <div className="flex flex-col gap-1.5">
          <Label className="text-xs font-medium text-muted-foreground">
            Colour
          </Label>
          <ColorSwatchPicker
            value={state.color}
            onChange={(color) => setState({ ...state, color })}
          />
        </div>
      </div>
      <div className="flex justify-end gap-2">
        <Button size="sm" variant="ghost" onClick={onCancel} className="gap-1">
          <X className="size-3.5" />
          Cancel
        </Button>
        <Button
          size="sm"
          onClick={submit}
          disabled={submitting}
          className="gap-1.5"
        >
          {submitting ? (
            <Loader2 className="size-4 animate-spin" />
          ) : (
            <Plus className="size-3.5" />
          )}
          {submitLabel}
        </Button>
      </div>
    </div>
  );
}
